import { twMerge } from 'tailwind-merge'

import { isAskUserFeedbackStagedAction } from '@renderer/chat/integrations/ask-user-feedback'
import { ResolvedAnswer } from '@renderer/chat/integrations/ask-user-card'
import {
  ACTION_HEADER_ICON_CENTER_CLASS,
  ACTION_HEADER_INDENT_CLASS,
} from '@renderer/overlay/Sidepanel/components/action-header'
import { ActionViewFeedbackCard } from '@renderer/overlay/Sidepanel/views/action-view/nested/feedback-card'
import { ActionViewHeader } from '@renderer/overlay/Sidepanel/views/action-view/nested/header'
import { ActionViewQuestionCard } from '@renderer/overlay/Sidepanel/views/action-view/nested/question-card'
import { useActionViewModel } from '@renderer/overlay/Sidepanel/views/action-view/use-action-view-model'
import type { ResolvedQuestionOutput } from '../chat-output'
import { AssistantText, OutputCaption, OutputMeetingSource } from './caption-pill'

export function ResolvedQuestionOutputs({
  actionId,
  questions,
  emptyAnswerLabel,
  assistantResponse,
  meetingAudioNoteId,
  connectToNext,
  threadOpen,
  onOpenThread,
}: {
  actionId: string
  questions: Array<ResolvedQuestionOutput>
  emptyAnswerLabel?: string
  assistantResponse?: string
  meetingAudioNoteId?: string
  connectToNext: boolean
  threadOpen: boolean
  onOpenThread: () => void
}) {
  const model = useActionViewModel(actionId)

  if (!model.action) {
    return model.isLoading ? <p className="text-foreground-secondary type-ui-sm px-4 py-6">Loading answers…</p> : null
  }

  return questions.map(({ question, answer }, index) => {
    const connectsAfter = index < questions.length - 1 || connectToNext

    return (
      <section
        key={`${question.header}-${index}`}
        aria-label={question.question}
        data-staged-action=""
        data-completed=""
        className="group/staged relative"
      >
        {connectsAfter && (
          <div
            aria-hidden="true"
            className={twMerge(
              'pointer-events-none absolute -bottom-3 top-8 border-l border-dashed',
              ACTION_HEADER_ICON_CENTER_CLASS,
            )}
          />
        )}
        <ActionViewHeader
          model={model}
          showSource={false}
          titleOverride={question.question}
          annotation={
            index === 0 && meetingAudioNoteId ? <OutputMeetingSource audioNoteId={meetingAudioNoteId} /> : undefined
          }
          className="py-2"
        />
        <div className={twMerge('pb-2', ACTION_HEADER_INDENT_CLASS)}>
          <ResolvedAnswer answers={answer.selected} explanation={answer.explanation} emptyLabel={emptyAnswerLabel} />
        </div>
        {assistantResponse && index === questions.length - 1 && (
          <div className={twMerge('pb-3', ACTION_HEADER_INDENT_CLASS)}>
            <AssistantText text={assistantResponse} threadOpen={threadOpen} onOpenThread={onOpenThread} />
          </div>
        )}
      </section>
    )
  })
}

export function StagedQuestionOutput({
  actionId,
  label,
  caption,
  meetingAudioNoteId,
  focused,
  standalone,
  connectToNext,
  threadOpen,
  onFocus,
  onOpenThread,
}: {
  actionId: string
  label: string
  caption: string | undefined
  meetingAudioNoteId?: string
  focused: boolean
  standalone: boolean
  connectToNext: boolean
  threadOpen: boolean
  onFocus: () => void
  onOpenThread: () => void
}) {
  const model = useActionViewModel(actionId)

  if (!model.action) {
    return model.isLoading ? <p className="text-foreground-secondary type-ui-sm px-4 py-6">Loading question…</p> : null
  }

  const isArchived = model.status === 'archived'

  return (
    // The connector hangs off the wrapper, not the card, so it runs past the caption to the next row.
    <div className="relative">
      {connectToNext && (
        <div
          aria-hidden="true"
          className={twMerge(
            'pointer-events-none absolute -bottom-3 top-8 border-l border-dashed',
            ACTION_HEADER_ICON_CENTER_CLASS,
          )}
        />
      )}
      <section
        aria-label={label}
        data-staged-action=""
        data-focused={focused ? '' : undefined}
        onPointerDownCapture={onFocus}
        onFocusCapture={onFocus}
        className="group/staged relative"
      >
        <ActionViewHeader
          model={model}
          showSource={false}
          isArchived={isArchived}
          annotation={meetingAudioNoteId ? <OutputMeetingSource audioNoteId={meetingAudioNoteId} /> : undefined}
          className="py-2"
        />
        {!isArchived &&
          (isAskUserFeedbackStagedAction(model.action) ? (
            <ActionViewFeedbackCard action={model.action} embedded deferPendingAnswer />
          ) : (
            <ActionViewQuestionCard action={model.action} embedded deferPendingAnswer />
          ))}
      </section>
      {caption && !isArchived && (
        <OutputCaption text={caption} standalone={standalone} threadOpen={threadOpen} onOpenThread={onOpenThread} />
      )}
    </div>
  )
}
